import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Download, TrendingDown } from 'lucide-react';
import AdminLayout from '../../../components/admin/AdminLayout';
import { useAdminAuth } from '../../../contexts/AdminAuthContext';

const ExpenseReportsPage = () => {
  const navigate = useNavigate();
  const { hasPermission } = useAdminAuth();
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('month');
  const [category, setCategory] = useState('all');
  const [expenses, setExpenses] = useState([]);

  useEffect(() => {
    setTimeout(() => {
      const mockExpenses = [
        { id: 'EXP-001', date: '2024-12-01', vendor: 'شركة المحاماة', category: 'legal', amount: 8000 },
        { id: 'EXP-002', date: '2024-12-03', vendor: 'شركة البرمجيات', category: 'technical', amount: 15000 },
        { id: 'EXP-003', date: '2024-11-28', vendor: 'مكتب التسويق', category: 'marketing', amount: 12000 }
      ];
      setExpenses(mockExpenses);
      setLoading(false);
    }, 500);
  }, [period, category]);

  const categoryLabels = { legal: 'قانوني', administrative: 'إداري', technical: 'تقني', marketing: 'تسويق', operational: 'تشغيلي' };

  const filteredExpenses = expenses.filter(e => category === 'all' || e.category === category);
  const totalExpense = filteredExpenses.reduce((sum, e) => sum + e.amount, 0);

  const categoryTotals = expenses.reduce((acc, e) => {
    acc[e.category] = (acc[e.category] || 0) + e.amount;
    return acc;
  }, {});

  const handleExport = (format) => {
    alert(`تصدير تقرير المصروفات بصيغة ${format}`);
  };

  if (loading) {
    return <AdminLayout><div className="flex items-center justify-center h-full"><div className="w-16 h-16 border-4 border-[#1E3A8A] border-t-transparent rounded-full animate-spin"></div></div></AdminLayout>;
  }

  return (
    <AdminLayout>
      <div className="p-6 max-w-7xl mx-auto">
        <div className="mb-6">
          <button onClick={() => navigate('/admin/finance/reports')} className="inline-flex items-center gap-2 text-gray-600 hover:text-[#1E3A8A] mb-4 transition-colors">
            <ArrowRight className="w-5 h-5" /><span className="font-semibold">العودة للتقارير</span>
          </button>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-black text-[#1E3A8A] mb-2 flex items-center gap-3">
                <TrendingDown className="w-8 h-8" /><span>تقارير المصروفات</span>
              </h1>
              <p className="text-gray-600">تحليل المصروفات حسب التصنيف والفترة</p>
            </div>
            {hasPermission('finance.reports.export') && (
              <div className="flex items-center gap-3">
                <button onClick={() => handleExport('PDF')} className="inline-flex items-center gap-2 px-4 py-2 border-2 border-[#1E3A8A] text-[#1E3A8A] rounded-lg font-bold hover:bg-blue-50 transition-all">
                  <Download className="w-4 h-4" /><span>PDF</span>
                </button>
                <button onClick={() => handleExport('Excel')} className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg font-bold hover:bg-green-700 transition-all">
                  <Download className="w-4 h-4" /><span>Excel</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">الفترة</label>
              <select value={period} onChange={(e) => setPeriod(e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none">
                <option value="month">هذا الشهر</option>
                <option value="year">هذا العام</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">التصنيف</label>
              <select value={category} onChange={(e) => setCategory(e.target.value)} className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-[#1E3A8A] focus:outline-none">
                <option value="all">جميع التصنيفات</option>
                <option value="legal">قانوني</option>
                <option value="technical">تقني</option>
                <option value="marketing">تسويق</option>
              </select>
            </div>
          </div>
        </div>

        {/* Category Breakdown */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {Object.entries(categoryTotals).map(([cat, total]) => (
            <div key={cat} className="bg-white rounded-lg shadow-sm border-2 border-red-200 p-4">
              <p className="text-sm text-gray-600 mb-1">{categoryLabels[cat]}</p>
              <p className="text-2xl font-black text-red-600">{total.toLocaleString()} ر.س</p>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 rounded-lg p-6 mb-6 text-white">
          <h3 className="text-lg font-bold mb-2">إجمالي المصروفات</h3>
          <p className="text-4xl font-black">{totalExpense.toLocaleString()} ر.س</p>
        </div>

        {/* Table */}
        <div className="bg-white rounded-lg shadow-sm border-2 border-gray-200 overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 border-b-2 border-gray-200">
              <tr>
                <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">التاريخ</th>
                <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">الجهة</th>
                <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">التصنيف</th>
                <th className="px-4 py-3 text-right text-sm font-bold text-gray-700">المبلغ</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {filteredExpenses.map(e => (
                <tr key={e.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">{e.date}</td>
                  <td className="px-4 py-3 text-sm font-semibold">{e.vendor}</td>
                  <td className="px-4 py-3 text-sm">{categoryLabels[e.category]}</td>
                  <td className="px-4 py-3 text-lg font-bold text-red-600">{e.amount.toLocaleString()} ر.س</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </AdminLayout>
  );
};

export default ExpenseReportsPage;